import { createContentfulClient } from '../contentful';

/**
 * 全ページ共通で使用するContentfulデータを取得
 * API呼び出し回数を削減するため、共通データを一括取得
 */
export async function getCommonContentfulData() {
    const client = createContentfulClient();
    
    try {
        // 複数のコンテンツタイプを並行して取得
        const [footerBanner, messages] = await Promise.all([
            client.getEntries({
                content_type: 'serviceSiteFooterBanner',
            }),
            client.getEntries({
                content_type: 'serviceSiteTopMetaNews',
                order: 'fields.order',
            }),
        ]);

        return {
            footerBanner,
            messages,
        };
    } catch (error) {
        console.error('Error fetching common Contentful data:', error);
        return {
            footerBanner: null,
            messages: null,
        };
    }
}

/**
 * ホームページ用のContentfulデータを一括取得
 * 複数のAPI呼び出しを1つにまとめることで、API利用回数を削減
 */
export async function getHomePageContentfulData() {
    const client = createContentfulClient();
    
    try {
        // 複数のコンテンツタイプを並行して取得
        const [
            messages,
            megaBanners,
            subBanners,
            yapTitle,
            yapEntries,
            signatureSalads,
            serviceBanners,
        ] = await Promise.all([
            client.getEntries({
                content_type: 'serviceSiteTopMetaNews',
                order: 'fields.order',
            }),
            client.getEntries({
                content_type: 'serviceSiteTopMegaBanner',
                order: 'fields.order',
            }),
            client.getEntries({
                content_type: 'serviceSiteTopSubBanner',
                order: 'fields.order',
            }),
            client.getEntries({
                content_type: 'serviceSiteSpecialSaladTitle',
                limit: 1,
            }),
            client.getEntries({
                content_type: 'serviceSiteSpecialSalad',
                order: 'fields.order',
                limit: 3,
            }),
            client.getEntries({
                content_type: 'serviceSiteSignatureSalads',
                order: 'fields.order',
                limit: 12,
            }),
            client.getEntries({
                content_type: 'serviceSiteTopBanner',
                order: 'fields.order',
            }),
        ]);

        return {
            messages,
            megaBanners,
            subBanners,
            yapTitle,
            yapEntries,
            signatureSalads,
            serviceBanners,
        };
    } catch (error) {
        console.error('Error fetching home page Contentful data:', error);
        return {
            messages: null,
            megaBanners: null,
            subBanners: null,
            yapTitle: null,
            yapEntries: null,
            signatureSalads: null,
            serviceBanners: null,
        };
    }
}

const NUTRITION_ENTRIES_TTL_MS = 30 * 1000;
let nutritionEntriesCache = null;

/**
 * 栄養成分表（serviceSiteNutrition）のエントリを公開日時の新しい順に取得
 * 成功した結果は 30 秒だけ使う。失敗したときは前回の一覧へ戻さない
 */
export async function getNutritionEntries() {
    if (
        nutritionEntriesCache &&
        Date.now() - nutritionEntriesCache.fetchedAt < NUTRITION_ENTRIES_TTL_MS
    ) {
        return nutritionEntriesCache.entries;
    }

    try {
        const client = createContentfulClient({
            // 3 秒 × 2 回だと再試行の待ちと合わせて 8 秒を超え、PDF 取得の前に締め切りが来る。
            // 2 秒 × 2 回 + 再試行前の待ち（約 2 秒）で、最悪でも約 6 秒に収める。
            timeout: 2000,
            retryLimit: 1,
        });
        const response = await client.getEntries({
            content_type: 'serviceSiteNutrition',
            order: '-fields.publishAt,-sys.updatedAt',
            limit: 20,
        });
        nutritionEntriesCache = { entries: response.items, fetchedAt: Date.now() };
        return response.items;
    } catch (error) {
        console.error('Error fetching nutrition entries:', error);
        return null;
    }
}

/**
 * メニュー一覧（serviceSiteMenuList）のエントリを公開日時の新しい順に取得
 * 取得に失敗した場合は null を返す
 */
export async function getMenuListEntries() {
    try {
        const client = createContentfulClient();
        const response = await client.getEntries({
            content_type: 'serviceSiteMenuList',
            order: '-fields.publishAt,-sys.updatedAt',
            limit: 20,
        });
        return response.items;
    } catch (error) {
        console.error('Error fetching menu list entries:', error);
        return null;
    }
}

/**
 * 従来のgetServiceSiteFooterBanner関数（下位互換性のため）
 * @deprecated 代わりにgetCommonContentfulDataを使用してください
 */
export async function getServiceSiteFooterBanner(client) {
    return await client
        .getEntries({
            content_type: 'serviceSiteFooterBanner',
        })
        .catch((error) => {
            console.error('Error fetching footer banner:', error);
            return error;
        });
}